/**
 * The dashboard shell — SPEC §9.3, §9.4.
 *
 * Holds the admin session, the cycle picker, and the two views built so far. Deliberately
 * separate from the instructor app: an admin browsing the portal as themselves is normal for
 * Luke, and the two identities have their own cookies and their own guards so neither can
 * stand in for the other.
 */

import { useCallback, useEffect, useState } from 'react';
import { adminApi, type AdminIdentity } from '../../lib/api.js';
import { AdminSignIn } from './AdminSignIn.js';
import { Coverage } from './Coverage.js';
import { CycleView } from './CycleView.js';
import { Roster } from './Roster.js';
import { Activity } from './Activity.js';
import type { RosterInstructorRow } from '../../lib/api.js';

type Tab = 'cycle' | 'coverage' | 'roster' | 'activity';

export function AdminApp() {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('cycle');
  const [cycles, setCycles] = useState<{ cycleKey: string; label: string }[]>([]);
  const [cycleKey, setCycleKey] = useState<string | null>(null);

  /** Set when Activity was opened from a roster row, so it lands already narrowed. */
  const [historyFor, setHistoryFor] = useState<RosterInstructorRow | null>(null);

  useEffect(() => {
    void adminApi
      .me()
      .then(({ admin: found }) => setAdmin(found))
      .catch(() => setAdmin(null))
      .finally(() => setLoading(false));
  }, []);

  const loadCycles = useCallback(async () => {
    const { cycles: list, required } = await adminApi.cycles();
    setCycles(list);
    setCycleKey((current) => current ?? required);
  }, []);

  useEffect(() => {
    if (admin) void loadCycles().catch(() => undefined);
  }, [admin, loadCycles]);

  if (loading) return <p className="muted center">Loading…</p>;
  if (!admin) return <AdminSignIn />;

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <div className="months" role="tablist" aria-label="Dashboard views">
          <button
            role="tab"
            className="months__tab"
            aria-current={tab === 'cycle'}
            onClick={() => setTab('cycle')}
          >
            Who owes me
          </button>
          <button
            role="tab"
            className="months__tab"
            aria-current={tab === 'coverage'}
            onClick={() => setTab('coverage')}
          >
            Coverage
          </button>
          <button
            role="tab"
            className="months__tab"
            aria-current={tab === 'roster'}
            onClick={() => setTab('roster')}
          >
            Roster
          </button>
          <button
            role="tab"
            className="months__tab"
            aria-current={tab === 'activity'}
            onClick={() => {
              // Arriving by the tab means "show me everything", so an instructor filter
              // left over from a roster row is cleared rather than silently still applied.
              setHistoryFor(null);
              setTab('activity');
            }}
          >
            Activity
          </button>
        </div>

        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          {tab !== 'roster' && tab !== 'activity' && cycles.length > 0 && cycleKey && (
            <label className="field" style={{ minWidth: 150 }}>
              <span className="field__label">Month</span>
              <select
                className="select"
                value={cycleKey}
                onChange={(e) => setCycleKey(e.target.value)}
              >
                {cycles.map((c) => (
                  <option key={c.cycleKey} value={c.cycleKey}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* Neither the roster nor the activity log is a month, so neither reads nor waits for
          the cycle. Activity is handed one anyway, but only so the month export knows which
          month to write. */}
      {tab === 'roster' ? (
        <Roster
          admin={admin}
          onViewHistory={(row) => {
            setHistoryFor(row);
            setTab('activity');
          }}
        />
      ) : tab === 'activity' ? (
        <Activity
          cycleKey={cycleKey}
          {...(historyFor
            ? {
                instructorId: historyFor.id,
                instructorName: historyFor.name,
                onClearInstructor: () => setHistoryFor(null),
              }
            : {})}
        />
      ) : (
        cycleKey &&
        (tab === 'cycle' ? <CycleView cycleKey={cycleKey} /> : <Coverage cycleKey={cycleKey} />)
      )}

      <p className="small muted center">
        Signed in as {admin.name ?? admin.email}
        {/* Role is shown because it changes what the person can do, and a Coordinator
            hitting a 403 they did not expect is worse than being told up front (§9.4). */}
        {admin.role === 'COORDINATOR' && ' · Coordinator (view and chase only)'}
        {' · '}
        <button
          type="button"
          className="btn btn--ghost"
          style={{ padding: 0, minHeight: 'auto' }}
          onClick={async () => {
            await adminApi.signOut().catch(() => undefined);
            setAdmin(null);
          }}
        >
          Sign out
        </button>
      </p>
    </div>
  );
}
